import React from "react";
import { connect } from "react-redux";
import {
  serviceCaller,
  withModals,
  ServiceCallerProps,
  ModalProps,
  withLoginRedirect,
} from "../../composition";
import RequestItem from "../requestItem";
import { firebaseConnect, isLoaded } from "react-redux-firebase";
import firebase from "firebase/compat/app";
import IRequest from "../../../types/services/IRequest";
import get from "lodash.get";
import IUser from "../../../types/services/IUser";
import Fragment from "../../../toolkit/fragment";
import { ScaleLoader } from "react-spinners";
import ISortByType from "../../../types/services/ISortByType";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";

interface Props extends ServiceCallerProps, ModalProps {
  firebase: firebase.app.App;
  requests: IRequest[];
  requestInboxes: string[];
  users: IUser[];
  sortBy: ISortByType;
  sortAsc: boolean;
  userId: string;
}

interface State {
  sentRequests: IRequest[];
  loadingRequests: boolean;
  inboxData: IRequest[];
  dataRelated: boolean;
}

class RequestList extends React.PureComponent<Props, State> {
  state = {
    sentRequests: [],
    loadingRequests: true,
    inboxData: [],
    dataRelated: false,
  };

  componentDidUpdate(prevProps: Props) {
    if (this.props.currentCharity !== prevProps.currentCharity) {
      this.setState({
        sentRequests: [],
        inboxData: [],
      });
    }
    if (this.props.requests !== prevProps.requests && this.props.users) {
      let newRequestList = [];
      if (this.props.requests) {
        this.props.requests.forEach(item => {
          const value = {
            ...item,
            senderInfo: this.getSenderInfo(item),
            recipientsInfo: this.getRecipientsInfo(item),
          };
          if (!newRequestList.some(request => request.id === value.id)) {
            newRequestList.push(value);
          }
        });

        this.setState({
          sentRequests: newRequestList,
        });
      } else {
        this.setState({
          sentRequests: [],
        });
      }
    }

    if (this.props.requestInboxes !== prevProps.requestInboxes) {
      this.getUserRequests();
    }
  }

  getSenderInfo(request: IRequest): IUser {
    if (this.props.users) {
      const users = this.props.users.filter(
        user => user.id === request.senderId
      );
      return users.length > 0 ? users[0] : undefined;
    }
    return null;
  }

  getRecipientsInfo(request: IRequest): IUser[] {
    return this.props.users.filter(user => request.recipients[user.id]);
  }

  getUserRequests() {
    this.setState(
      {
        loadingRequests: false,
      },
      () => {
        const charityId = this.props.currentCharity.id;
        if (this.props.requestInboxes) {
          Promise.all(
            this.props.requestInboxes.map(requestId =>
              this.props.firebase
                .database()
                .ref()
                .child(this.props.currentCharity.id)
                .child("requests")
                .child(requestId)
                .once("value")
                .then(snapshot => {
                  return {
                    ...snapshot.val(),
                    senderInfo: this.getSenderInfo(snapshot.val() as IRequest),
                    recipientsInfo: this.getRecipientsInfo(
                      snapshot.val() as IRequest
                    ),
                    id: snapshot.key,
                  };
                })
            )
          ).then(inboxData => {
            //This check is needed in case the user switches charities while loading
            if (this.props.currentCharity.id === charityId) {
              this.setState({
                inboxData: inboxData,
              });
            }
          });
        } else {
          this.setState({
            inboxData: [],
          });
        }
      }
    );
  }

  mergeAndSort = (): IRequest[] => {
    const merged = this.state.sentRequests.concat(this.state.inboxData);
    //Sort everything
    merged.sort((first, second) => {
      const firstValue = first[this.props.sortBy.colName];
      const secondValue = second[this.props.sortBy.colName];
      let comparison = 0;
      if (typeof firstValue === "string") {
        comparison = firstValue.localeCompare(secondValue);
      } else {
        comparison = firstValue - secondValue;
      }
      return (this.props.sortAsc ? 1 : -1) * comparison;
    });
    return merged;
  };

  render() {
    if (
      isLoaded(this.props.requests) &&
      isLoaded(this.props.requestInboxes) &&
      this.props.users
    ) {
      const list = this.mergeAndSort();
      return (
        <Fragment>
          {list.length > 0 &&
            list.map((request, index) => (
              <RequestItem
                request={request}
                index={index}
                key={index}
                user={request.senderInfo}
              />
            ))}
          {list.length === 0 && <div className={s.emptyList}>No Requests</div>}
        </Fragment>
      );
    } else {
      return (
        <div className="d-flex full-width justify-content-center m-t-200 m-b-300">
          <ScaleLoader color={"#1f65b1"} loading />
        </div>
      );
    }
  }
}

const mapStateToProps = state => {
  const { firebase, user } = state;
  const inboxes = !get(
    firebase,
    `ordered.${user.currentCharity.id}.requestInboxes.${user.id}`
  )
    ? null
    : get(
        firebase,
        `ordered.${user.currentCharity.id}.requestInboxes.${user.id}`
      ).map(userObject => userObject.key);
  return {
    requests: !get(firebase, `ordered.${user.currentCharity.id}.requests`)
      ? null
      : get(firebase, `ordered.${user.currentCharity.id}.requests`).map(
          requestObject => {
            return {
              ...requestObject.value,
              id: requestObject.key,
            };
          }
        ),
    users: !get(firebase, `ordered.${user.currentCharity.id}.users`)
      ? null
      : get(firebase, `ordered.${user.currentCharity.id}.users`).map(
          userObject => {
            return {
              ...userObject.value,
              id: userObject.key,
            };
          }
        ),
    userId: user.id,
    requestInboxes: inboxes,
  };
};

export default connect<Props>(
  mapStateToProps,
  null
)(
  serviceCaller(
    withLoginRedirect(
      withModals(
        firebaseConnect((_, store) => {
          return store.getState().user.currentCharity.id !== ""
            ? [
                {
                  path: `${store.getState().user.currentCharity.id}/requests`,
                  queryParams: [
                    "orderByChild=senderId",
                    `equalTo=${store.getState().user.id}`,
                  ],
                },
                `${store.getState().user.currentCharity.id}/users`,
                `${store.getState().user.currentCharity.id}/requestInboxes/${
                  store.getState().user.id
                }`,
              ]
            : [];
        })(withStyles(s)(RequestList))
      )
    )
  )
);
